import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Globe, Save, RefreshCw, History, Home, Info, Phone, HelpCircle,
  Shield, FileText, ChevronDown, ChevronUp, Zap, RotateCcw, MapPin,
  Building, MessageSquare, Sparkles, Layout, Plus, Trash2, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';

// ─── Collapsible Section ───
const Section = ({ title, icon: Icon, color = "#10B981", children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06] overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 dark:bg-white/[0.02] transition-colors"
      >
        <div className="flex items-center gap-2.5">
          {Icon && <Icon className="w-4 h-4" style={{ color }} />}
          <span className="text-gray-900 dark:text-white font-outfit font-semibold text-sm">{title}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
      </button>
      {open && (
        <CardContent className="pt-0 pb-5 px-5 space-y-4 border-t border-gray-100 dark:border-white/[0.04]">
          {children}
        </CardContent>
      )}
    </Card>
  );
};

// ─── Field helpers ───
const Field = ({ label, hint, children }) => (
  <div className="space-y-1">
    <Label className="text-gray-700 dark:text-gray-300 text-xs font-semibold">{label}</Label>
    {hint && <p className="text-[10px] text-zinc-500 mb-1">{hint}</p>}
    {children}
  </div>
);

const TF = ({ value, onChange, placeholder, rows }) =>
  rows ? (
    <Textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows}
      className="bg-white dark:bg-[#07090E] border-gray-300 dark:border-white/[0.12] text-gray-900 dark:text-white text-sm mt-1 resize-none" />
  ) : (
    <Input value={value} onChange={onChange} placeholder={placeholder}
      className="bg-white dark:bg-[#07090E] border-gray-300 dark:border-white/[0.12] text-gray-900 dark:text-white text-sm mt-1" />
  );

const SiteContentManager = () => {
  const [SC, setSC] = useState({});
  const [revisions, setRevisions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("home");
  const [faqPageTarget, setFaqPageTarget] = useState("home");

  // Course FAQs editing state
  const COURSES_LIST = [
    { slug: "smart-trader-elementary", name: "Smart Trader Elementary (Free)" },
    { slug: "technical-analysis-course", name: "Super Trader Edge Course" },
    { slug: "options-trading-course", name: "Super Option Analysis Course" },
    { slug: "technical-analysis-options-combo-course", name: "Super Trader Combo" },
  ];
  const [selectedCourseSlug, setSelectedCourseSlug] = useState("smart-trader-elementary");
  const [courseFaqsList, setCourseFaqsList] = useState([]);
  const [loadingCourseFaqs, setLoadingCourseFaqs] = useState(false);
  const [savingCourseFaqs, setSavingCourseFaqs] = useState(false);

  const fetchCourseFaqs = async (slug) => {
    setLoadingCourseFaqs(true);
    try {
      const res = await axios.get(`${API}/admin/courses/${slug}/full`, { withCredentials: true });
      const rawFaqs = res.data?.meta?.faqs || [];
      const normalized = rawFaqs.map(f => ({
        q: f.q || f.question || "",
        a: f.a || f.answer || ""
      }));
      setCourseFaqsList(normalized);
    } catch (err) {
      try {
        const pRes = await axios.get(`${API}/courses/detail/${slug}`);
        const pRaw = pRes.data?.meta?.faqs || [];
        setCourseFaqsList(pRaw.map(f => ({ q: f.q || f.question || "", a: f.a || f.answer || "" })));
      } catch (e2) {
        toast.error("Could not load course FAQs: " + err.message);
      }
    } finally {
      setLoadingCourseFaqs(false);
    }
  };

  useEffect(() => {
    if (faqPageTarget === "courses") {
      fetchCourseFaqs(selectedCourseSlug);
    }
  }, [faqPageTarget, selectedCourseSlug]);

  const saveCourseFaqs = async () => {
    setSavingCourseFaqs(true);
    try {
      await axios.put(
        `${API}/admin/courses/${selectedCourseSlug}/meta`,
        { faqs: courseFaqsList },
        { withCredentials: true }
      );
      const cObj = COURSES_LIST.find(c => c.slug === selectedCourseSlug);
      toast.success(`✅ Saved ${courseFaqsList.length} FAQs for ${cObj?.name || selectedCourseSlug}!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save course FAQs: " + err.message);
    } finally {
      setSavingCourseFaqs(false);
    }
  };

  useEffect(() => { fetchConfig(); fetchRevisions(); }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/site-config`);
      setSC(res.data || {});
    } catch { toast.error("Failed to load site config"); }
    finally { setLoading(false); }
  };

  const fetchRevisions = async () => {
    try {
      const res = await axios.get(`${API}/site-config/revisions`, { withCredentials: true });
      setRevisions(res.data || []);
    } catch {}
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/site-config`, SC, { withCredentials: true });
      toast.success("✅ All changes saved to website!");
      fetchRevisions();
    } catch (err) { 
      toast.error(err.response?.data?.detail || "Failed to save: " + err.message); 
    }
    finally { setSaving(false); }
  };

  const restore = async (rev_id) => {
    if (!window.confirm("Restore this version?")) return;
    try {
      await axios.post(`${API}/site-config/revisions/${rev_id}/restore`, {}, { withCredentials: true });
      toast.success("Restored!"); fetchConfig(); fetchRevisions();
    } catch { toast.error("Restore failed"); }
  };

  const upd = (key, val) => setSC(prev => ({ ...prev, [key]: val }));
  const f = (key, placeholder, rows) => (
    <TF value={SC[key] || ""} onChange={e => upd(key, e.target.value)} placeholder={placeholder} rows={rows} />
  );

  if (loading) return (
    <div className="py-20 flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      <p className="text-zinc-500 text-xs">Loading content...</p>
    </div>
  );

  return (
    <div className="space-y-0 pb-20">
      {/* ─── Sticky Header ─── */}
      <div className="sticky top-0 z-20 bg-gray-50 dark:bg-[#0A0E17]/95 backdrop-blur border-b border-gray-200 dark:border-white/[0.06] px-1 py-3 mb-6 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-gray-900 dark:text-white font-outfit font-bold flex items-center gap-2 text-base">
            <Globe className="w-5 h-5 text-emerald-600 dark:text-emerald-400" /> Site Content Control Panel
          </h2>
          <p className="text-[11px] text-zinc-500 mt-0.5">Edit any field → click Save All Changes → live on website instantly.</p>
        </div>
        <Button onClick={save} disabled={saving} className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-xs rounded-xl transition-all px-5">
          {saving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save All Changes</>}
        </Button>
      </div>

      {/* ─── Page Tabs ─── */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto pb-2 border-b border-gray-200 dark:border-white/[0.06] mb-6 custom-scrollbar">
          <TabsList className="bg-transparent border-0 p-0 h-auto gap-1 inline-flex min-w-max">
            {[
              { val: "home", label: "🏠 Home", },
              { val: "drishti", label: "⚡ Drishti AI" },
              { val: "about", label: "ℹ️ About Us" },
              { val: "contact", label: "📞 Contact" },
              { val: "refund", label: "💸 Refund Policy" },
              { val: "faq", label: "❓ FAQ" },
              { val: "privacy", label: "🔒 Privacy Policy" },
              { val: "terms", label: "📄 Terms" },
              { val: "header-footer", label: "⚙️ Header & Footer" },
              { val: "cities", label: "🏙️ City Pages" },
              { val: "revisions", label: "🕐 Revision History" },
            ].map(t => (
              <TabsTrigger key={t.val} value={t.val}
                className="text-xs px-4 py-2 rounded data-[state=active]:bg-emerald-50 dark:data-[state=active]:bg-white/[0.08] data-[state=active]:text-emerald-700 dark:data-[state=active]:text-white text-zinc-500 hover:text-gray-900 dark:hover:text-white transition-colors font-medium">
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {/* ═══════════════════════════════════
            HOME PAGE
        ═══════════════════════════════════ */}
        <TabsContent value="home" className="space-y-3 mt-0">
          <p className="text-xs text-zinc-500 mb-4 px-1">Control the <strong className="text-zinc-300">Home Page (jamadhan.in)</strong> content below.</p>

          <Section title="Hero Section — Top of page, first thing visitors see" icon={Home} defaultOpen={true}>
            <Field label="Hero Main Heading" hint="Big headline. e.g. 'See The Market Before It Moves'">
              {f("hero_heading", "See The Market Before It Moves")}
            </Field>
            <Field label="Hero Sub-description" hint="Paragraph below the heading">
              {f("hero_description", "With Drishti AI — our proprietary trading intelligence...", 3)}
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Primary Button Text">{f("cta_primary_text", "Enroll Now")}</Field>
              <Field label="Secondary Button Text">{f("cta_secondary_text", "Watch Demo")}</Field>
              <Field label="Tertiary Button Text">{f("cta_tertiary_text", "Join Telegram")}</Field>
            </div>
            <Field label="Telegram Link" hint="Used for Join Telegram button">
              {f("telegram_link", "https://t.me/...")}
            </Field>
          </Section>

          <Section title="Stats Bar — Numbers shown under hero (e.g. 10000+ Students)">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="space-y-2 p-3 rounded border border-gray-200 dark:border-white/5 bg-black/20">
                  <p className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold">Stat {i}</p>
                  <Field label="Number (e.g. 10,000+)">{f(`stat_${i}_number`, "10,000+")}</Field>
                  <Field label="Label (e.g. Students Trained)">{f(`stat_${i}_label`, "Students Trained")}</Field>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Features Section — 'Why JamaDhan' cards">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="p-3 rounded border border-gray-200 dark:border-white/5 bg-black/20 space-y-2">
                <p className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold">Feature Card {i}</p>
                <Field label="Card Title">{f(`feature_${i}_title`, "Expert Instructors")}</Field>
                <Field label="Card Description">{f(`feature_${i}_desc`, "Learn from active professionals...", 2)}</Field>
              </div>
            ))}
          </Section>

          <Section title="Testimonials / Reviews Section — student testimonial heading">
            <Field label="Section Heading">{f("testimonials_heading", "What Our Students Say")}</Field>
            <Field label="Section Sub-heading">{f("testimonials_subheading", "Join thousands of successful traders...", 2)}</Field>
          </Section>

          <Section title="Call To Action (CTA) — bottom section of home page">
            <Field label="CTA Heading">{f("cta_heading", "Ready to Make the Market Work for You?")}</Field>
            <Field label="CTA Description">{f("cta_description", "Join thousands of traders who chose knowledge over luck.", 2)}</Field>
            <Field label="Primary Button Text">{f("cta_btn_primary", "Enroll Now")}</Field>
            <Field label="Secondary Button Text">{f("cta_btn_secondary", "Download Brochure")}</Field>
          </Section>

          <Section title="Home Page SEO — What shows on Google search results">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="SEO Title" hint="Shown in Google search results tab">{f("home_seo_title", "Jamadhan | India's Best Stock Market Institute")}</Field>
              <Field label="SEO Description" hint="Shown under the title in Google">{f("home_seo_description", "Learn stock market trading...", 3)}</Field>
            </div>
            <Field label="JSON-LD Schema (Advanced — leave blank if unsure)">
              <Textarea value={SC.home_schema || ""} onChange={e => upd("home_schema", e.target.value)}
                rows={4} placeholder='{"@context": "https://schema.org", "@type": "WebPage", ...}'
                className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-emerald-600 dark:text-emerald-400 font-mono text-xs mt-1" />
            </Field>
          </Section>
        </TabsContent>

        {/* ═══════════════════════════════════
            DRISHTI AI PAGE
        ═══════════════════════════════════ */}
        <TabsContent value="drishti" className="space-y-3 mt-0">
          <p className="text-xs text-zinc-500 mb-4 px-1">Control the <strong className="text-zinc-300">Drishti AI Landing Page (/drishti-ai)</strong> content, pricing, setups, and FAQs below.</p>

          <Section title="Drishti AI Hero Section" icon={Zap} defaultOpen={true}>
            <Field label="Eyebrow Tagline" hint="Small label above heading">
              {f("drishti_hero_tagline", "PROPRIETARY TRADING INTELLIGENCE")}
            </Field>
            <Field label="Hero Main Heading" hint="Big headline. e.g. 'See The Market Before It Moves'">
              {f("drishti_hero_heading", "See The Market Before It Moves")}
            </Field>
            <Field label="Hero Sub-description" hint="Full description paragraph">
              {f("drishti_hero_description", "With Drishti AI — our proprietary trading intelligence layer built into the Jamadhan ecosystem. Scan institutional demand and supply zones with real-time price tracking.", 3)}
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Primary CTA Button Text">{f("drishti_cta_btn_primary", "Request Pro Access")}</Field>
              <Field label="Secondary Button Text">{f("drishti_cta_btn_secondary", "Explore Setups")}</Field>
            </div>
            <Field label="Demo / Walkthrough Video URL" hint="YouTube or MP4 video URL for demo">
              {f("drishti_demo_video_url", "/drishti-robo-feature.mp4")}
            </Field>
          </Section>

          <Section title="Setup Classifications (MIT, QIT, DIT, HYIT)">
            <p className="text-xs text-zinc-500 -mt-2">Explain each trade setup type displayed on Drishti AI.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-3 rounded border border-gray-200 dark:border-white/5 bg-black/20 space-y-2">
                <p className="text-xs font-bold text-emerald-500">MIT Setup</p>
                <Field label="MIT Description">{f("drishti_mit_desc", "A structured setup type triggered when price behavior aligns with MIT conditions near key zones.", 2)}</Field>
              </div>
              <div className="p-3 rounded border border-gray-200 dark:border-white/5 bg-black/20 space-y-2">
                <p className="text-xs font-bold text-sky-500">QIT Setup</p>
                <Field label="QIT Description">{f("drishti_qit_desc", "Used when the market forms a qualifying setup based on QIT logic and confirmation criteria.", 2)}</Field>
              </div>
              <div className="p-3 rounded border border-gray-200 dark:border-white/5 bg-black/20 space-y-2">
                <p className="text-xs font-bold text-amber-500">DIT Setup</p>
                <Field label="DIT Description">{f("drishti_dit_desc", "Designed for setups that match defined DIT conditions near key zones.", 2)}</Field>
              </div>
              <div className="p-3 rounded border border-gray-200 dark:border-white/5 bg-black/20 space-y-2">
                <p className="text-xs font-bold text-purple-500">HYIT Setup</p>
                <Field label="HYIT Description">{f("drishti_hyit_desc", "For high-quality opportunities where multiple supporting conditions align.", 2)}</Field>
              </div>
            </div>
          </Section>

          <Section title="How Drishti Works (4 Steps)">
            {[1, 2, 3, 4].map(s => (
              <div key={s} className="p-3 rounded border border-gray-200 dark:border-white/5 bg-black/20 space-y-2">
                <p className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold">Step {s}</p>
                <Field label={`Step ${s} Title`}>{f(`drishti_step_${s}_title`, s === 1 ? "Market Scans Zones" : s === 2 ? "Tracks Price Position" : s === 3 ? "Waits for Setup Confirmation" : "Sends Structured Alerts")}</Field>
                <Field label={`Step ${s} Description`}>{f(`drishti_step_${s}_desc`, "", 2)}</Field>
              </div>
            ))}
          </Section>

          <Section title="Pricing & Subscription Gating Display">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Monthly Pro Display Price">{f("drishti_monthly_price", "₹999 / 30 Days")}</Field>
              <Field label="Quarterly Pro Display Price">{f("drishti_quarterly_price", "₹2,499 / 90 Days")}</Field>
              <Field label="Annual Pro Display Price">{f("drishti_annual_price", "₹7,999 / 365 Days")}</Field>
            </div>
            <Field label="WhatsApp Enrollment Phone" hint="WhatsApp contact phone for access approval requests">
              {f("drishti_whatsapp_phone", "9257059361")}
            </Field>
          </Section>

          <Section title="Drishti AI FAQs">
            {[1, 2, 3, 4].map(q => (
              <div key={q} className="p-3 rounded border border-gray-200 dark:border-white/5 bg-black/20 space-y-2">
                <Field label={`FAQ ${q} Question`}>{f(`drishti_faq_${q}_q`, "")}</Field>
                <Field label={`FAQ ${q} Answer`}>{f(`drishti_faq_${q}_a`, "", 2)}</Field>
              </div>
            ))}
          </Section>
        </TabsContent>

        {/* ═══════════════════════════════════
            ABOUT PAGE
        ═══════════════════════════════════ */}
        <TabsContent value="about" className="space-y-3 mt-0">
          <p className="text-xs text-zinc-500 mb-4 px-1">Control the <strong className="text-zinc-300">About Us Page (/about)</strong> content below.</p>

          <Section title="Hero Section — First screen visitors see on About page" defaultOpen={true}>
            <Field label="Main Hero Heading" hint="Big headline at the top">
              {f("about_hero_heading", "We Don't Predict Markets. We Master Them.")}
            </Field>
            <Field label="Hero Description Paragraph">
              {f("about_hero_description", "India's leading stock market institute...", 3)}
            </Field>
          </Section>

          <Section title="Stats Bar — 4 numbers shown on About page">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { key: "about_stat_1_value", klabel: "about_stat_1_label", dv: "12+", dl: "Years of Expertise" },
                { key: "about_stat_2_value", klabel: "about_stat_2_label", dv: "10,000+", dl: "Traders Transformed" },
                { key: "about_stat_3_value", klabel: "about_stat_3_label", dv: "6", dl: "Markets Covered" },
                { key: "about_stat_4_value", klabel: "about_stat_4_label", dv: "ISO", dl: "Certified 21001:2018" },
              ].map((s, i) => (
                <div key={i} className="space-y-2 p-3 rounded border border-gray-200 dark:border-white/5 bg-black/20">
                  <Field label="Number">{f(s.key, s.dv)}</Field>
                  <Field label="Label">{f(s.klabel, s.dl)}</Field>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Our Story Section — Story text and founder quote">
            <Field label="Story Heading">{f("about_story_heading", "Born to Break the Myth")}</Field>
            <Field label="Story Description" hint="The main body text. Press Enter for new paragraphs.">
              {f("about_story_description", "JamaDhan was created with a single, powerful purpose...", 6)}
            </Field>
            <Field label="Founder Quote" hint="Shown in the quote box with Deepak Sethia's name">
              {f("about_founder_quote", "The stock market is not a casino. It's a science — and science can be learned.")}
            </Field>
          </Section>

          <Section title="Vision & Mission Section">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 p-3 rounded border border-gray-200 dark:border-white/5 bg-black/20">
                <p className="text-[10px] text-emerald-600 dark:text-emerald-400 uppercase tracking-widest font-bold">Our Vision</p>
                <Field label="Vision Heading">{f("about_vision_heading", "Trading Is Not Gambling")}</Field>
                <Field label="Vision Description">{f("about_vision_desc", "We envision a world where stock trading is recognised...", 3)}</Field>
              </div>
              <div className="space-y-2 p-3 rounded border border-gray-200 dark:border-white/5 bg-black/20">
                <p className="text-[10px] text-cyan-600 dark:text-cyan-400 uppercase tracking-widest font-bold">Our Mission</p>
                <Field label="Mission Heading">{f("about_mission_heading", "Build a Strong Foundation First")}</Field>
                <Field label="Mission Description">{f("about_mission_desc", "Just as a building's strength starts with its foundation...", 3)}</Field>
              </div>
            </div>
          </Section>

          <Section title="Instructor Section — Deepak Sethia's profile">
            <Field label="Section Heading">{f("about_mentor_heading", "Deepak Doesn't Teach Tips. He Teaches Truth.")}</Field>
            <Field label="Mentor Name">{f("about_mentor_name", "Deepak Sethia")}</Field>
            <Field label="Mentor Title / Role">{f("about_mentor_role", "Founder & Head Mentor")}</Field>
            <Field label="Mentor Bio" hint="Main bio text under the instructor section">
              {f("about_mentor_bio", "With over 12 years of live trading experience...", 4)}
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="space-y-2 p-3 rounded border border-gray-200 dark:border-white/5 bg-black/20">
                  <p className="text-[10px] text-zinc-500 uppercase font-bold">Stat {i}</p>
                  <Field label="Value">{f(`about_mentor_stat_${i}_val`, "12+")}</Field>
                  <Field label="Label">{f(`about_mentor_stat_${i}_label`, "Years Experience")}</Field>
                </div>
              ))}
            </div>
          </Section>

          <Section title="About Page SEO">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="SEO Title">{f("about_seo_title", "About Jamadhan | India's Premier Stock Market Institute")}</Field>
              <Field label="SEO Description">{f("about_seo_description", "Learn about JamaDhan...", 3)}</Field>
            </div>
          </Section>
        </TabsContent>

        {/* ═══════════════════════════════════
            CONTACT PAGE
        ═══════════════════════════════════ */}
        <TabsContent value="contact" className="space-y-3 mt-0">
          <p className="text-xs text-zinc-500 mb-4 px-1">Control the <strong className="text-zinc-300">Contact Page (/contact)</strong> content below.</p>

          <Section title="Contact Page Content" defaultOpen={true}>
            <Field label="Page Heading">{f("contact_heading", "Get In Touch With Us")}</Field>
            <Field label="Page Sub-description">{f("contact_subheading", "We're here to answer your questions...", 2)}</Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Email Address">{f("contact_email", "info@jamadhan.in")}</Field>
              <Field label="Phone Number">{f("contact_phone", "+91 98765 43210")}</Field>
            </div>
            <Field label="Office Address">{f("contact_address", "JamaDhan Institute, Jaipur, Rajasthan", 2)}</Field>
            <Field label="Google Maps Embed URL" hint="Paste the embed URL from Google Maps">
              {f("contact_map_url", "https://www.google.com/maps/embed?pb=...")}
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="WhatsApp Link">{f("contact_whatsapp", "https://wa.me/91...")}</Field>
              <Field label="Telegram Link">{f("contact_telegram", "https://t.me/...")}</Field>
              <Field label="Instagram Link">{f("contact_instagram", "https://instagram.com/...")}</Field>
            </div>
          </Section>

          <Section title="Contact Page SEO">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="SEO Title">{f("contact_seo_title", "Contact Jamadhan | Get In Touch")}</Field>
              <Field label="SEO Description">{f("contact_seo_description", "Contact JamaDhan...", 3)}</Field>
            </div>
          </Section>
        </TabsContent>

        {/* ═══════════════════════════════════
            FAQ MANAGER ACROSS ALL PAGES
        ═══════════════════════════════════ */}
        <TabsContent value="faq" className="space-y-4 mt-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3.5 rounded-2xl bg-black/30 border border-white/10">
            <div>
              <p className="text-sm font-bold text-white font-outfit">Edit Existing FAQs for Any Page</p>
              <p className="text-xs text-zinc-400">Select which page's FAQs you want to view, modify, or add to.</p>
            </div>
            <div className="flex flex-wrap gap-1.5 p-1 rounded-xl bg-black/50 border border-white/10">
              {[
                { id: "home", label: "🏠 Home Page" },
                { id: "courses", label: "📚 Course FAQs" },
                { id: "faq_page", label: "❓ FAQ Page (/faq)" },
                { id: "drishti", label: "⚡ Drishti AI" },
                { id: "cities", label: "🏙️ City Pages" },
              ].map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setFaqPageTarget(t.id)}
                  className={`text-xs px-3 py-1.5 rounded-lg font-semibold transition-all ${
                    faqPageTarget === t.id
                      ? "bg-[#00FF88] text-black shadow-sm font-bold"
                      : "text-zinc-400 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* 1. HOME PAGE FAQS */}
          {faqPageTarget === "home" && (
            <div className="space-y-3">
              <Section title="Home Page FAQ Header" defaultOpen={true}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Eyebrow Text">{f("home_faq_eyebrow", "GOT QUESTIONS?")}</Field>
                  <Field label="Main Heading">{f("home_faq_heading", "Frequently Asked Questions")}</Field>
                </div>
              </Section>

              <Section title="Home Page FAQ Items (Questions & Answers)" defaultOpen={true}>
                <p className="text-xs text-zinc-400 mb-3">
                  Edit the 5 default home page FAQs below or add up to 10. Leave empty to hide an item.
                </p>
                {[
                  {
                    i: 1,
                    defQ: "I have never done any trade in stock market, Can I Join now?",
                    defA: "At Jamadhan, we have courses for people with all levels of knowledge, from those who are just starting out to those who are now experts. If you're new to the stock market, you can start with our introductory classes to learn the basics of how it works. Our courses include a wide range of topics, such as technical analysis, trading options, trading stocks, trading foreign currencies, trading commodities, and more. Our skilled teachers teach you everything you need to know to help you reach your investment goals. Join us today and take the first step towards mastering the stock market."
                  },
                  {
                    i: 2,
                    defQ: "How we clarify our doubts after live class?",
                    defA: "At Jamadhan, we know how important it is for our students to have a personal mentor and help. So, we give each student a mentor, who will be there to answer any concerns or doubts you might have. Our trainers are professionals with a lot of expertise who know a lot about the stock market and how to invest. We also give you access to recordings of the courses, so you can go over the content at your own pace and when it suits you. Join us today and we'll help you do well on the stock market with personalised advice and aid whenever you need it."
                  },
                  {
                    i: 3,
                    defQ: "Is your technical study applied only on equity?",
                    defA: "Our strategy is universal, which means you may use it with any asset class (stocks, commodities, or foreign exchange)."
                  },
                  {
                    i: 4,
                    defQ: "What's the probability I can expect?",
                    defA: "It's up to the person. You need a lot of practise and patience to get better. After a while of practice, your aim will start to get better."
                  },
                  {
                    i: 5,
                    defQ: "How much gain I can expect after completion of course?",
                    defA: "It depends on how well you comprehend the ideas, how well you can control your emotions, and how much experience you have. It also relies on how eager you are to take risks. You might get anywhere from 2% to 100% a month, depending on how much risk you are willing to take."
                  },
                  { i: 6, defQ: "Do I get lifetime access to recorded classes and study notes?", defA: "Yes, all enrolled batch students receive lifetime access to class recordings, updated presentation slides, and study notes in their student portal." },
                  { i: 7, defQ: "Will I get live market practical training?", defA: "Yes, Deepak Sir conducts real-time live market chart breakdown sessions during actual market trading hours." },
                  { i: 8, defQ: "What trading tools and setups will I learn?", defA: "You will master institutional order blocks, demand & supply zones, volume profile, risk calculator, and Drishti AI algorithmic signals." },
                  { i: 9, defQ: "Can I trade part-time with a full-time job?", defA: "Yes, we teach swing trading and positional institutional setups that only require 15-20 minutes of evening analysis." },
                  { i: 10, defQ: "How do I get mentor support after enrollment?", defA: "You get access to private batch channels and direct mentor reviews for your trade journal and charts." },
                ].map(({ i, defQ, defA }) => (
                  <div key={i} className="p-3.5 rounded-xl border border-gray-200 dark:border-white/10 bg-black/20 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-emerald-400 uppercase font-mono tracking-wider">Home FAQ #{i}</span>
                      <span className="text-[10px] text-zinc-500">Live on Home Page (/)</span>
                    </div>
                    <Field label="Question">{f(`home_faq_${i}_q`, defQ)}</Field>
                    <Field label="Answer">{f(`home_faq_${i}_a`, defA, 3)}</Field>
                  </div>
                ))}
              </Section>
            </div>
          )}

          {/* COURSE FAQS */}
          {faqPageTarget === "courses" && (
            <div className="space-y-4">
              <Section title="Course FAQs Editor" icon={BookOpen} color="#00FF88" defaultOpen={true}>
                {/* Course selector tabs */}
                <div className="space-y-2 pb-2">
                  <Label className="text-xs font-semibold text-zinc-300">Select Course to Edit FAQs</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                    {COURSES_LIST.map((c) => (
                      <button
                        key={c.slug}
                        type="button"
                        onClick={() => setSelectedCourseSlug(c.slug)}
                        className={`p-3 rounded-xl text-left border transition-all text-xs font-outfit ${
                          selectedCourseSlug === c.slug
                            ? "bg-[#00FF88]/15 border-[#00FF88] text-[#00FF88] shadow-[0_0_15px_rgba(0,255,136,0.15)] font-bold"
                            : "bg-black/30 border-white/10 text-zinc-300 hover:border-white/25 hover:text-white"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <BookOpen className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate">{c.name}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Header bar with actions */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-3 pb-2 border-t border-white/[0.06]">
                  <div>
                    <h3 className="text-sm font-bold text-white font-outfit flex items-center gap-2">
                      <span>{COURSES_LIST.find(c => c.slug === selectedCourseSlug)?.name}</span>
                      <span className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-white/10 text-[#00FF88]">
                        {courseFaqsList.length} FAQs
                      </span>
                    </h3>
                    <p className="text-xs text-zinc-400">
                      Changes saved here update immediately on the live course detail page accordion.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setCourseFaqsList(prev => [
                          ...prev,
                          { q: "", a: "" }
                        ]);
                      }}
                      className="text-xs bg-white/5 border-white/15 text-white hover:bg-white/10 flex items-center gap-1.5"
                    >
                      <Plus className="w-3.5 h-3.5 text-[#00FF88]" />
                      <span>Add Question</span>
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      disabled={savingCourseFaqs || loadingCourseFaqs}
                      onClick={saveCourseFaqs}
                      className="text-xs bg-[#00FF88] hover:bg-[#00FF88]/90 text-black font-bold flex items-center gap-1.5 shadow-[0_0_15px_rgba(0,255,136,0.3)]"
                    >
                      {savingCourseFaqs ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      <span>Save Course FAQs</span>
                    </Button>
                  </div>
                </div>

                {/* FAQ Items List */}
                {loadingCourseFaqs ? (
                  <div className="py-12 flex flex-col items-center justify-center gap-2">
                    <RefreshCw className="w-6 h-6 text-[#00FF88] animate-spin" />
                    <span className="text-xs text-zinc-400">Loading course FAQs...</span>
                  </div>
                ) : courseFaqsList.length === 0 ? (
                  <div className="text-center py-12 border border-dashed border-white/10 rounded-2xl bg-black/20 space-y-3">
                    <p className="text-sm text-zinc-400">No FAQs configured yet for this course.</p>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        setCourseFaqsList([{ q: "", a: "" }]);
                      }}
                      className="bg-[#00FF88] text-black font-semibold text-xs"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add First Question
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3 pt-2">
                    {courseFaqsList.map((faq, idx) => (
                      <div
                        key={idx}
                        className="p-4 rounded-xl border border-white/10 bg-[#060A12] space-y-3 relative group"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-[#00FF88] font-mono uppercase tracking-wider">
                            Question #{idx + 1}
                          </span>
                          <button
                            type="button"
                            title="Delete FAQ"
                            onClick={() => {
                              setCourseFaqsList(prev => prev.filter((_, i) => i !== idx));
                            }}
                            className="p-1.5 rounded-lg text-zinc-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs text-zinc-300">Question</Label>
                          <Input
                            value={faq.q}
                            onChange={(e) => {
                              const val = e.target.value;
                              setCourseFaqsList(prev => {
                                const next = [...prev];
                                next[idx] = { ...next[idx], q: val };
                                return next;
                              });
                            }}
                            placeholder="e.g. Do I need prior trading experience?"
                            className="bg-black/40 border-white/10 text-white text-xs sm:text-sm"
                          />
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs text-zinc-300">Answer</Label>
                          <Textarea
                            value={faq.a}
                            onChange={(e) => {
                              const val = e.target.value;
                              setCourseFaqsList(prev => {
                                const next = [...prev];
                                next[idx] = { ...next[idx], a: val };
                                return next;
                              });
                            }}
                            placeholder="Enter the detailed answer..."
                            rows={3}
                            className="bg-black/40 border-white/10 text-white text-xs sm:text-sm resize-none"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            </div>
          )}

          {/* 2. MAIN FAQ PAGE (/faq) */}
          {faqPageTarget === "faq_page" && (
            <div className="space-y-3">
              <Section title="FAQ Page Header" defaultOpen={true}>
                <Field label="Page Heading">{f("faq_heading", "Frequently Asked Questions")}</Field>
                <Field label="Page Sub-description">{f("faq_subheading", "Everything you need to know about our courses and trading education.", 2)}</Field>
              </Section>

              <Section title="FAQ Page Items (Questions & Answers)" defaultOpen={true}>
                <p className="text-xs text-zinc-500 mb-3">Add up to 10 FAQs for the dedicated /faq page. Leave blank to hide.</p>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => (
                  <div key={i} className="p-3.5 rounded-xl border border-gray-200 dark:border-white/10 bg-black/20 space-y-2">
                    <p className="text-xs font-bold text-emerald-400 uppercase font-mono">FAQ #{i}</p>
                    <Field label="Question">{f(`faq_${i}_question`, `What is FAQ question ${i}?`)}</Field>
                    <Field label="Answer">{f(`faq_${i}_answer`, "Answer here...", 3)}</Field>
                  </div>
                ))}
              </Section>

              <Section title="FAQ Page SEO">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="SEO Title">{f("faq_seo_title", "FAQ | Jamadhan Stock Market Institute")}</Field>
                  <Field label="SEO Description">{f("faq_seo_description", "Answers to common questions...", 3)}</Field>
                </div>
              </Section>
            </div>
          )}

          {/* 3. DRISHTI AI FAQS */}
          {faqPageTarget === "drishti" && (
            <div className="space-y-3">
              <Section title="Drishti AI Landing Page FAQs (/drishti-ai)" defaultOpen={true}>
                <p className="text-xs text-zinc-400 mb-3">Edit the 6 FAQs displayed on the Drishti AI landing page.</p>
                {[
                  { i: 1, defQ: "What is Drishti AI and how does it work?", defA: "Drishti AI is our proprietary trading intelligence terminal that scans 500+ Indian stocks in real-time for high-probability institutional demand and supply zones." },
                  { i: 2, defQ: "Do I need coding knowledge to use Drishti AI?", defA: "No coding or algorithmic knowledge is required. Drishti AI runs 100% in the cloud and displays visual trade signals with entry, target, and stop loss levels." },
                  { i: 3, defQ: "Does Drishti AI work for Intraday and Swing Trading?", defA: "Yes, Drishti AI supports intraday scans (5-minute and 15-minute) as well as multi-day positional and swing trading setups (1-hour and Daily)." },
                  { i: 4, defQ: "Can I use Drishti AI on mobile?", defA: "Yes, the Drishti AI web application is fully responsive and accessible on mobile browsers, tablets, and desktop." },
                  { i: 5, defQ: "How do course students get 3 months free access?", defA: "Students enrolled in Super Trader Edge, Super Option Analysis, or Super Trader Combo automatically receive 3 months of complimentary Drishti Pro access upon batch enrollment." },
                  { i: 6, defQ: "How do I upgrade or renew my subscription?", defA: "You can renew or upgrade your plan directly inside your student dashboard under the Drishti AI tab." },
                ].map(({ i, defQ, defA }) => (
                  <div key={i} className="p-3.5 rounded-xl border border-gray-200 dark:border-white/10 bg-black/20 space-y-2">
                    <p className="text-xs font-bold text-amber-400 uppercase font-mono">Drishti AI FAQ #{i}</p>
                    <Field label="Question">{f(`drishti_faq_${i}_q`, defQ)}</Field>
                    <Field label="Answer">{f(`drishti_faq_${i}_a`, defA, 3)}</Field>
                  </div>
                ))}
              </Section>
            </div>
          )}

          {/* 4. CITY LANDING PAGES FAQS */}
          {faqPageTarget === "cities" && (
            <div className="space-y-3">
              <Section title="City Landing Pages FAQs (Jaipur, Jodhpur, Udaipur, etc.)" defaultOpen={true}>
                <p className="text-xs text-zinc-400 mb-3">
                  Edit the FAQs shown across city landing pages. Use <code className="text-emerald-400 font-mono text-xs">{"{cityName}"}</code> to automatically insert the current city name (e.g. Jaipur, Jodhpur).
                </p>
                {[
                  { i: 1, defQ: "Are these stock market classes in {cityName} conducted offline or online?", defA: "We offer both classroom training at our center and interactive live online batches for traders in {cityName}." },
                  { i: 2, defQ: "Do I need prior trading experience to join in {cityName}?", defA: "No prior experience is necessary. We start from ground basics and progress to advanced institutional setups." },
                  { i: 3, defQ: "What are the timings of the classes?", defA: "We offer weekday evening batches and weekend morning batches to suit working professionals and students." },
                  { i: 4, defQ: "Will I get live market trading practice during market hours?", defA: "Yes, students participate in live market trading floor practice with mentors between 9:15 AM and 3:30 PM." },
                  { i: 5, defQ: "Do you offer placement support or certificate?", defA: "Yes, you receive a completion certificate and guidance on career pathways in financial advisory, research, and prop trading." },
                  { i: 6, defQ: "Can I attend a free demo session before enrolling?", defA: "Yes, you can register for our free introductory workshop or schedule a 1-on-1 counseling call with our mentors." },
                ].map(({ i, defQ, defA }) => (
                  <div key={i} className="p-3.5 rounded-xl border border-gray-200 dark:border-white/10 bg-black/20 space-y-2">
                    <p className="text-xs font-bold text-cyan-400 uppercase font-mono">City FAQ #{i}</p>
                    <Field label="Question">{f(`city_faq_${i}_q`, defQ)}</Field>
                    <Field label="Answer">{f(`city_faq_${i}_a`, defA, 3)}</Field>
                  </div>
                ))}
              </Section>
            </div>
          )}
        </TabsContent>

        {/* ═══════════════════════════════════
            PRIVACY POLICY
        ═══════════════════════════════════ */}
        <TabsContent value="privacy" className="space-y-3 mt-0">
          <p className="text-xs text-zinc-500 mb-4 px-1">Control the <strong className="text-zinc-300">Privacy Policy Page (/privacy-policy)</strong> content below.</p>

          <Section title="Privacy Policy Content" defaultOpen={true}>
            <Field label="Page Title">{f("privacy_title", "Privacy Policy")}</Field>
            <Field label="Last Updated Date">{f("privacy_last_updated", "January 1, 2025")}</Field>
            <Field label="Full Privacy Policy Text" hint="Paste your full privacy policy text here. Use line breaks for paragraphs.">
              {f("privacy_content", "Your privacy policy content here...", 15)}
            </Field>
          </Section>

          <Section title="Privacy Policy SEO">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="SEO Title">{f("privacy_seo_title", "Privacy Policy | Jamadhan")}</Field>
              <Field label="SEO Description">{f("privacy_seo_description", "Read our privacy policy...", 3)}</Field>
            </div>
          </Section>
        </TabsContent>

        {/* ═══════════════════════════════════
            TERMS & CONDITIONS
        ═══════════════════════════════════ */}
        <TabsContent value="terms" className="space-y-3 mt-0">
          <p className="text-xs text-zinc-500 mb-4 px-1">Control the <strong className="text-zinc-300">Terms & Conditions Page (/terms-and-conditions)</strong> content below.</p>

          <Section title="Terms & Conditions Content" defaultOpen={true}>
            <Field label="Page Title">{f("terms_title", "Terms & Conditions")}</Field>
            <Field label="Last Updated Date">{f("terms_last_updated", "January 1, 2025")}</Field>
            <Field label="Full Terms & Conditions Text" hint="Paste your full T&C text here. Use line breaks for paragraphs.">
              {f("terms_content", "Your terms and conditions content here...", 15)}
            </Field>
          </Section>

          <Section title="Terms Page SEO">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="SEO Title">{f("terms_seo_title", "Terms & Conditions | Jamadhan")}</Field>
              <Field label="SEO Description">{f("terms_seo_description", "Read our terms and conditions...", 3)}</Field>
            </div>
          </Section>
        </TabsContent>

        {/* ═══════════════════════════════════
            REFUND & CANCELLATION POLICY
        ═══════════════════════════════════ */}
        <TabsContent value="refund" className="space-y-3 mt-0">
          <p className="text-xs text-zinc-500 mb-4 px-1">Control the <strong className="text-zinc-300">Refund & Cancellation Policy (/refund-and-cancellation)</strong> content below.</p>

          <Section title="Policy Header & Highlights" icon={RotateCcw} defaultOpen={true}>
            <Field label="Page Title">{f("refund_policy_title", "Refund and Cancellation Policy")}</Field>
            <Field label="Eyebrow Subtitle">{f("refund_policy_eyebrow", "Fee Protection & Transparent Policies")}</Field>
            <Field label="Refund Window" hint="e.g. 7 Days or No Refund after session starts">{f("refund_window_days", "7 Calendar Days")}</Field>
            <Field label="Summary Statement" hint="Paragraph shown in the highlight card at top">{f("refund_policy_summary", "At JamaDhan Stock Market Institute, we strive to provide high-quality education and trading intelligence. We believe in complete transparency regarding our fee, refund, and cancellation terms.", 3)}</Field>
          </Section>

          <Section title="Eligibility Criteria & Cancellation Window">
            <Field label="Eligibility Terms" hint="Describe who can claim refund and under what conditions">
              {f("refund_eligibility_criteria", "Refund requests must be formally submitted within 7 calendar days of course enrollment or before accessing more than 20% of recorded material/live batches, whichever comes first.", 3)}
            </Field>
            <Field label="Non-Refundable Items" hint="Items or software that cannot be refunded (e.g. Drishti AI API, live seat reservation)">
              {f("refund_non_refundable_text", "Drishti AI active subscriptions, proprietary algorithmic terminal licenses, and completed 1-on-1 mentorship hours are strictly non-refundable once activated.", 3)}
            </Field>
          </Section>

          <Section title="Refund Process & Timelines">
            <Field label="Step-by-step Process">{f("refund_process_steps", "1. Email support@jamadhan.com with your registered email, transaction ID, and reason for cancellation.\n2. Our admissions desk reviews the request within 2 business days.\n3. Approved refunds are credited back to the original payment source within 5 to 7 working days.", 4)}</Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Grievance / Refund Email">{f("refund_support_email", "support@jamadhan.com")}</Field>
              <Field label="Grievance / Refund Phone">{f("refund_support_phone", "+91-9257059361")}</Field>
            </div>
          </Section>

          <Section title="Custom Legal Document Override (Advanced)">
            <Field label="Full Verbatim Policy Markdown / Text" hint="If entered, this overrides the default legal text rendered on /refund-and-cancellation">
              {f("refund_policy_full_text", "", 8)}
            </Field>
          </Section>
        </TabsContent>

        {/* ═══════════════════════════════════
            HEADER & FOOTER MANAGEMENT
        ═══════════════════════════════════ */}
        <TabsContent value="header-footer" className="space-y-3 mt-0">
          <p className="text-xs text-zinc-500 mb-4 px-1">Control the global <strong className="text-zinc-300">Header Navigation, Announcement Ticker, and Footer</strong> content below.</p>

          <Section title="Top Header & Announcement Bar" icon={Layout} defaultOpen={true}>
            <Field label="Scrolling Announcement Text" hint="Shown in the market ticker bar at top. Leave blank to hide.">
              {f("announcement_text", "🎓 Offline Batches Starting in Jaipur & Rajasthan — Limited Seats Available!")}
            </Field>
            <Field label="Announcement Click URL">{f("announcement_link", "/#courses")}</Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Header Primary CTA Button Text">{f("header_cta_text", "Start Learning")}</Field>
              <Field label="Header Primary CTA Button URL">{f("header_cta_link", "/#courses")}</Field>
            </div>
          </Section>

          <Section title="Footer Identity & Corporate Info" icon={Building}>
            <Field label="Footer Company Description" hint="Shown below the logo in the footer">
              {f("footer_description", "India's premier stock market trading institute empowering retail traders with institutional price action, quantitative tools, and disciplined risk management.", 3)}
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Helpline Phone">{f("local_seo_phone", "+91-9257059361")}</Field>
              <Field label="Official Email">{f("local_seo_email", "contactus@jamadhan.com")}</Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <Field label="Street Address">{f("local_seo_street", "3rd Floor, Ajmer Road, Opp MG Motors")}</Field>
              <Field label="City">{f("local_seo_city", "Jaipur")}</Field>
              <Field label="State / Region">{f("local_seo_region", "Rajasthan")}</Field>
              <Field label="Postal / PIN Code">{f("local_seo_postal", "302021")}</Field>
            </div>
          </Section>

          <Section title="SEBI Disclaimer & Risk Disclosures" icon={Shield}>
            <Field label="SEBI Disclaimer Text" hint="Mandatory stock market education disclaimer shown on all pages">
              {f("footer_disclaimer", "Disclaimer: JamaDhan is an educational platform and stock market training institute. We are NOT registered SEBI investment advisors. All strategies, market briefings, and educational materials provided are for learning purposes only. Stock and derivatives trading involve substantial risk of loss.", 4)}
            </Field>
            <Field label="Copyright Line">{f("footer_copyright", "JamaDhan Stock Market Institute. All rights reserved.")}</Field>
          </Section>

          <Section title="Social Media Profiles">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="YouTube Channel URL">{f("social_youtube", "https://youtube.com/@jamadhan")}</Field>
              <Field label="Instagram Profile URL">{f("social_instagram", "https://instagram.com/jamadhan")}</Field>
              <Field label="Telegram Channel URL">{f("social_telegram", "https://t.me/+QOePMys7V481NmZl")}</Field>
              <Field label="Twitter / X Profile URL">{f("social_twitter", "https://x.com/jamadhan")}</Field>
              <Field label="Facebook Page URL">{f("social_facebook", "https://facebook.com/jamadhan")}</Field>
              <Field label="LinkedIn Page URL">{f("social_linkedin", "https://linkedin.com/company/jamadhan")}</Field>
            </div>
          </Section>

          <Section title="Global SEO Defaults">
            <Field label="Default Meta Description" hint="Used when a page doesn't have its own description">
              {f("global_seo_description", "JamaDhan — India's premier stock market trading institute empowering traders with institutional price action and Drishti AI.", 3)}
            </Field>
            <Field label="Global OG Image URL" hint="Image shown when sharing site on social media">
              {f("global_og_image", "https://jamadhan.com/hero-bg.webp")}
            </Field>
          </Section>
        </TabsContent>

        {/* ═══════════════════════════════════
            CITY LANDING PAGES
        ═══════════════════════════════════ */}
        <TabsContent value="cities" className="space-y-3 mt-0">
          <p className="text-xs text-zinc-500 mb-4 px-1">Control default templates and counselor phone for <strong className="text-zinc-300">City Landing Pages (/stock-market-course-in-:citySlug)</strong>.</p>

          <Section title="Regional City Page Templates" icon={MapPin} defaultOpen={true}>
            <Field label="City Page Hero Heading Template" hint="Use {city} placeholder. e.g. 'Best Stock Market Course in {city}'">
              {f("city_hero_title_template", "Best Stock Market Course in {city} | Offline Trading Academy")}
            </Field>
            <Field label="City Page Hero Description Template" hint="Use {city} placeholder">
              {f("city_hero_desc_template", "Join the top-rated offline institutional trading institute in {city}. Master demand and supply zones, price action, and options strategies with Deepak Sir.", 3)}
            </Field>
            <Field label="Counselor WhatsApp Phone" hint="Phone number called/messaged from city pages">
              {f("city_whatsapp_number", "9257059361")}
            </Field>
            <Field label="Offline Center Key Highlights" hint="Comma-separated or bullet list of offline features">
              {f("city_center_features", "Live Trading Floor Access, 1-on-1 Chart Mentorship, Lifetime Community Membership, Offline Doubt Clearing Desks", 2)}
            </Field>
          </Section>
        </TabsContent>

        {/* ═══════════════════════════════════
            REVISION HISTORY
        ═══════════════════════════════════ */}
        <TabsContent value="revisions" className="mt-0">
          <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06]">
            <CardHeader>
              <CardTitle className="text-gray-900 dark:text-white font-outfit text-base flex items-center gap-2">
                <History className="w-5 h-5 text-emerald-600 dark:text-emerald-400" /> Revision History
              </CardTitle>
              <p className="text-xs text-zinc-500">Every time you save, a snapshot is created. You can restore any previous version.</p>
            </CardHeader>
            <CardContent>
              {revisions.length === 0 ? (
                <p className="text-zinc-600 text-sm py-6 text-center">No revisions yet. Save your first change to create a snapshot.</p>
              ) : (
                <div className="space-y-2">
                  {revisions.map((rev, idx) => {
                    const date = new Date(rev.saved_at);
                    return (
                      <div key={rev.revision_id} className="flex items-center justify-between p-3 rounded border border-gray-200 dark:border-white/5 bg-black/20 hover:bg-black/40 transition-colors">
                        <div>
                          <p className="text-sm text-gray-900 dark:text-white font-medium">
                            {idx === 0 ? <span className="text-emerald-600 dark:text-emerald-400">● Current Version</span> : `Version ${revisions.length - idx}`}
                            {rev.note && <span className="ml-2 text-xs text-orange-400">({rev.note})</span>}
                          </p>
                          <p className="text-xs text-zinc-500 mt-0.5">{date.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                        {idx !== 0 && (
                          <Button size="sm" variant="outline" onClick={() => restore(rev.revision_id)}
                            className="bg-gray-100 dark:bg-white/5 border-gray-200 dark:border-white/10 text-gray-900 dark:text-white hover:bg-emerald-50 dark:hover:bg-emerald-950/40 hover:text-emerald-600 dark:hover:text-emerald-400 hover:border-emerald-300 dark:hover:border-emerald-800">
                            ↩ Restore
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SiteContentManager;
